import React, { useCallback, useEffect, useState, useRef } from 'react';
import { StickyNote, Sparkles, MessageSquarePlus, ZoomIn, ZoomOut, GripVertical, X } from 'lucide-react';
const GRID_SIZE = 24;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;
interface NotepadWidget {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
}
function snapToGrid(value: number): number {
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}
export function NotepadView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState({
    x: 0,
    y: 0
  });
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({
    x: 0,
    y: 0,
    panX: 0,
    panY: 0
  });
  // Demo widgets (empty by default — AI will populate)
  const [widgets, setWidgets] = useState<NotepadWidget[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragStart = useRef({
    x: 0,
    y: 0,
    widgetX: 0,
    widgetY: 0
  });
  // --- Pan handlers ---
  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    // Only pan on direct canvas click (not on widgets)
    if (e.target !== e.currentTarget && !(e.target as HTMLElement).dataset.canvas) return;
    e.preventDefault();
    setIsPanning(true);
    panStart.current = {
      x: e.clientX,
      y: e.clientY,
      panX: pan.x,
      panY: pan.y
    };
  }, [pan]);
  useEffect(() => {
    if (!isPanning) return;
    const handleMove = (e: MouseEvent) => {
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      setPan({
        x: panStart.current.panX + dx,
        y: panStart.current.panY + dy
      });
    };
    const handleUp = () => setIsPanning(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isPanning]);
  // --- Zoom handler ---
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom(z => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + delta)));
  }, []);
  // --- Widget drag handlers ---
  const handleWidgetDragStart = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const widget = widgets.find(w => w.id === id);
    if (!widget) return;
    setDraggingId(id);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      widgetX: widget.x,
      widgetY: widget.y
    };
  }, [widgets]);
  useEffect(() => {
    if (!draggingId) return;
    const handleMove = (e: MouseEvent) => {
      const dx = (e.clientX - dragStart.current.x) / zoom;
      const dy = (e.clientY - dragStart.current.y) / zoom;
      setWidgets(prev => prev.map(w => w.id === draggingId ? {
        ...w,
        x: snapToGrid(dragStart.current.widgetX + dx),
        y: snapToGrid(dragStart.current.widgetY + dy)
      } : w));
    };
    const handleUp = () => setDraggingId(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [draggingId, zoom]);
  const removeWidget = (id: string) => {
    setWidgets(prev => prev.filter(w => w.id !== id));
  };
  const resetView = () => {
    setPan({
      x: 0,
      y: 0
    });
    setZoom(1);
  };
  const gridDotSize = GRID_SIZE * zoom;
  const dotColor = 'rgba(148,163,184,0.35)';
  const darkDotColor = 'rgba(100,116,139,0.25)';
  const isEmpty = widgets.length === 0;
  return <div ref={containerRef} className="relative w-full overflow-hidden select-none" style={{
    height: 'calc(100vh - 64px)',
    cursor: isPanning ? 'grabbing' : draggingId ? 'grabbing' : 'grab'
  }} onMouseDown={handleCanvasMouseDown} onWheel={handleWheel} data-id="element-481">
      {/* Dot grid background */}
      <div className="absolute inset-0 pointer-events-none" data-canvas="true" style={{
      backgroundImage: `radial-gradient(circle, var(--dot-color) 1px, transparent 1px)`,
      backgroundSize: `${gridDotSize}px ${gridDotSize}px`,
      backgroundPosition: `${pan.x % gridDotSize}px ${pan.y % gridDotSize}px`,
      // @ts-ignore
      '--dot-color': dotColor
    } as React.CSSProperties} data-id="element-482" />
      {/* Dark mode overlay for dots */}
      <div className="absolute inset-0 pointer-events-none hidden dark:block" data-canvas="true" style={{
      backgroundImage: `radial-gradient(circle, ${darkDotColor} 1px, transparent 1px)`,
      backgroundSize: `${gridDotSize}px ${gridDotSize}px`,
      backgroundPosition: `${pan.x % gridDotSize}px ${pan.y % gridDotSize}px`
    }} data-id="element-483" />

      {/* Transform layer for widgets */}
      <div className="absolute" data-canvas="true" style={{
      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
      transformOrigin: '0 0'
    }} data-id="element-484">
        {widgets.map(widget => <div key={widget.id} className={`absolute bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg shadow-md overflow-hidden ${draggingId === widget.id ? 'ring-2 ring-primary-400 shadow-lg' : ''}`} style={{
        left: widget.x,
        top: widget.y,
        width: widget.w,
        height: widget.h
      }} data-id="element-485">
            {/* Widget header — drag handle */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-dark-hover border-b border-gray-100 dark:border-dark-border cursor-grab active:cursor-grabbing" onMouseDown={e => handleWidgetDragStart(e, widget.id)} data-id="element-486">
              <div className="flex items-center gap-1.5" data-id="element-487">
                <GripVertical size={12} className="text-gray-400" data-id="element-488" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-489">
                  {widget.title}
                </span>
              </div>
              <button onClick={() => removeWidget(widget.id)} className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-dark-border text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" data-id="element-490">
                <X size={12} data-id="element-491" />
              </button>
            </div>
            {/* Widget body — empty placeholder for AI content */}
            <div className="p-3 h-full flex items-center justify-center text-xs text-gray-400 dark:text-gray-500" data-id="element-492">
              AI-generated content
            </div>
          </div>)}
      </div>

      {/* Empty state — centered in viewport */}
      {isEmpty && <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10" data-id="element-493">
          <div className="pointer-events-auto text-center px-6" data-id="element-494">
            <div className="w-14 h-14 rounded-2xl bg-primary-500/10 dark:bg-primary-500/20 flex items-center justify-center mb-5 mx-auto" data-id="element-495">
              <StickyNote size={28} className="text-primary-500" data-id="element-496" />
            </div>

            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2" data-id="element-497">
              AI Notepad
            </h2>

            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mb-6 leading-relaxed" data-id="element-498">
              Your infinite canvas workspace. Ask the assistant to generate
              charts, summaries, or comparisons — widgets will snap into place
              here.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8 justify-center" data-id="element-499">
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 bg-white/80 dark:bg-dark-card/80 backdrop-blur-sm border border-gray-100 dark:border-dark-border rounded-lg px-3 py-2" data-id="element-500">
                <Sparkles size={13} className="text-primary-400 shrink-0" data-id="element-501" />
                <span data-id="element-502">"Show me a detailed spending breakdown"</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 bg-white/80 dark:bg-dark-card/80 backdrop-blur-sm border border-gray-100 dark:border-dark-border rounded-lg px-3 py-2" data-id="element-503">
                <MessageSquarePlus size={13} className="text-primary-400 shrink-0" data-id="element-504" />
                <span data-id="element-505">"Compare my investments side by side"</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 justify-center" data-id="element-506">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" data-id="element-507" />
              Open the chat assistant to get started
            </div>
          </div>
        </div>}

      {/* Zoom controls — top-right */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-0.5 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg shadow-sm p-0.5" data-id="element-508">
        <button onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP))} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-500 dark:text-gray-400 transition-colors" title="Zoom out" data-id="element-509">
          <ZoomOut size={14} data-id="element-510" />
        </button>
        <button onClick={resetView} className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 w-11 text-center tabular-nums rounded px-1 py-1 hover:bg-gray-100 dark:hover:bg-dark-hover transition-colors" title="Reset to 100%" data-id="element-511">
          {Math.round(zoom * 100)}%
        </button>
        <button onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP))} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-500 dark:text-gray-400 transition-colors" title="Zoom in" data-id="element-512">
          <ZoomIn size={14} data-id="element-513" />
        </button>
      </div>
    </div>;
}